import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import ReactCountryFlag from "react-country-flag";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import swal from "sweetalert";
import api from "../../api/api";
import Background from "../../assets/images/background-signup.png";
import logo from "../../assets/images/Logo smart farm1.jpg";

const languages = {
  ar: { label: "AR", flag: "TN" },
  fr: { label: "FR", flag: "FR" },
  en: { label: "EN", flag: "GB" },
  it: { label: "IT", flag: "IT" },
  wf: { label: "WF", flag: "SN" },
};

export const Register = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [local, setLocal] = useState(localStorage.getItem("local") || "en");
  const [name, setName] = useState("");
  const [num, setNum] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [offerType, setOfferType] = useState("");
  const [hasAccepted, setAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({ name: "", email: "", password: "", confirm: "" });

  const changeLanguageHandler = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("local", lang);
    setLocal(lang);
  };

  const validate = () => {
    const nextErrors = { name: "", email: "", password: "", confirm: "" };
    if (!name.trim()) nextErrors.name = "Username cannot be blank!";
    if (!email.trim()) nextErrors.email = t("email_cannot_be_blank");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) nextErrors.email = t("incorrect_email_format");
    if (password.length < 6) nextErrors.password = t("password_too_short");
    if (password !== confirmPass) nextErrors.confirm = t("passwords_dont_match");
    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const resetForm = () => {
    setName("");
    setEmail("");
    setNum("");
    setPassword("");
    setConfirmPass("");
    setOfferType("");
    setAccepted(false);
    setErrors({ name: "", email: "", password: "", confirm: "" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const response = await api.post("/signup", {
        name,
        email,
        phone_number: num,
        password,
        confirmPassword: confirmPass,
        offer_type: offerType,
      });

      if (response.data.type === "danger") {
        swal({ icon: "error", title: t("Oops..."), text: response.data.message });
        return;
      }

      if (response.data.type === "warnning") {
        swal({ icon: "warning", title: t("Oops..."), text: response.data.message });
        return;
      }

      if (response.data.type === "success") {
        resetForm();
        await swal({
          icon: "success",
          title: t("ok"),
          text: t("email_sent_check_inbox_confirm"),
        });
        navigate("/");
      }
    } catch (error) {
      swal({ icon: "error", title: t("Oops..."), text: t("error_add_user") });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateField = (setter, errorKey) => (event) => {
    setter(event.target.value);
    if (errors[errorKey]) setErrors((current) => ({ ...current, [errorKey]: "" }));
  };

  return (
    <main className="auth-page auth-register-page">
      <div className="auth-shell auth-shell-wide">
        <section className="auth-visual" style={{ backgroundImage: `url(${Background})` }}>
          <div className="auth-visual-shade" />
          <Link to="/" className="auth-brand" aria-label="SmartFarm">
            <img src={logo} alt="SmartFarm" />
          </Link>
          <div className="auth-visual-content">
            <span className="auth-eyebrow">SMARTFARM</span>
            <h1>{t("agriculture_intelligente")}</h1>
            <p>Créez votre espace et transformez les données du terrain en décisions simples.</p>
            <div className="auth-feature-list">
              <span><i className="fas fa-broadcast-tower" />{t("sensors")}</span>
              <span><i className="far fa-calendar-alt" />{t("calendar")}</span>
              <span><i className="far fa-lightbulb" />{t("recommendations")}</span>
            </div>
          </div>
          <div className="auth-visual-footer">
            <i className="fas fa-seedling" />
            <span>Commencez simplement, évoluez à votre rythme</span>
          </div>
        </section>

        <section className="auth-form-panel">
          <div className="auth-form-topbar">
            <span className="auth-secure-label"><i className="fas fa-leaf" /> SmartFarm</span>
            <Dropdown align="end" className="auth-language">
              <Dropdown.Toggle variant="light" id="register-language">
                <ReactCountryFlag countryCode={languages[local].flag} svg />
                <span>{languages[local].label}</span>
                <i className="fas fa-chevron-down" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {Object.entries(languages).map(([lang, language]) => (
                  <Dropdown.Item key={lang} active={local === lang} onClick={() => changeLanguageHandler(lang)}>
                    <ReactCountryFlag countryCode={language.flag} svg />
                    <span>{language.label}</span>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <div className="auth-form-content auth-register-content">
            <div className="auth-form-heading">
              <span className="auth-heading-icon"><i className="fas fa-user-plus" /></span>
              <div>
                <span className="auth-eyebrow">SMARTFARM</span>
                <h2>{t("Sign up")}</h2>
                <p>{t("already_account")} <Link to="/">{t("sign_in")}</Link></p>
              </div>
            </div>

            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="auth-form-grid">
                <label className="auth-field">
                  <span>{t("name")}</span>
                  <div className={`auth-input-wrap ${errors.name ? "is-invalid" : ""}`}>
                    <i className="far fa-user" />
                    <input type="text" value={name} placeholder={t("name")} autoComplete="name" onChange={updateField(setName, "name")} />
                  </div>
                  {errors.name && <small className="auth-field-error"><i className="fas fa-exclamation-circle" />{errors.name}</small>}
                </label>

                <label className="auth-field">
                  <span>{t("email")}</span>
                  <div className={`auth-input-wrap ${errors.email ? "is-invalid" : ""}`}>
                    <i className="far fa-envelope" />
                    <input type="email" value={email} placeholder="name@example.com" autoComplete="email" onChange={updateField(setEmail, "email")} />
                  </div>
                  {errors.email && <small className="auth-field-error"><i className="fas fa-exclamation-circle" />{errors.email}</small>}
                </label>

                <label className="auth-field">
                  <span>{t("Password")}</span>
                  <div className={`auth-input-wrap ${errors.password ? "is-invalid" : ""}`}>
                    <i className="fas fa-lock" />
                    <input type={showPassword ? "text" : "password"} value={password} placeholder="••••••••" autoComplete="new-password" onChange={updateField(setPassword, "password")} />
                    <button type="button" className="auth-password-toggle" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}>
                      <i className={`far ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                    </button>
                  </div>
                  {errors.password && <small className="auth-field-error"><i className="fas fa-exclamation-circle" />{errors.password}</small>}
                </label>

                <label className="auth-field">
                  <span>{t("ConfirmPassword")}</span>
                  <div className={`auth-input-wrap ${errors.confirm ? "is-invalid" : ""}`}>
                    <i className="fas fa-lock" />
                    <input type={showConfirmPassword ? "text" : "password"} value={confirmPass} placeholder="••••••••" autoComplete="new-password" onChange={updateField(setConfirmPass, "confirm")} />
                    <button type="button" className="auth-password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)} aria-label={showConfirmPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}>
                      <i className={`far ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`} />
                    </button>
                  </div>
                  {errors.confirm && <small className="auth-field-error"><i className="fas fa-exclamation-circle" />{errors.confirm}</small>}
                </label>

                <label className="auth-field">
                  <span>{t("num")}</span>
                  <PhoneInput
                    country="tn"
                    value={num}
                    onChange={setNum}
                    containerClass="auth-phone"
                    inputClass="auth-phone-input"
                    buttonClass="auth-phone-button"
                  />
                </label>

                <label className="auth-field">
                  <span>{t("select_offer")}</span>
                  <div className="auth-input-wrap auth-select-wrap">
                    <i className="fas fa-layer-group" />
                    <select value={offerType} required onChange={(event) => setOfferType(event.target.value)}>
                      <option value="" disabled>{t("select_offer")}</option>
                      <option value="1">{t("gratuit")}</option>
                      <option value="2">{t("payante")}</option>
                    </select>
                  </div>
                </label>
              </div>

              <label className="auth-checkbox auth-terms-check">
                <input type="checkbox" checked={hasAccepted} required onChange={(event) => setAccepted(event.target.checked)} />
                <span>{t("i_agree")}</span>
              </label>

              <button type="submit" className="auth-primary-button" disabled={isSubmitting || !hasAccepted}>
                {isSubmitting ? <i className="fas fa-circle-notch fa-spin" /> : <i className="fas fa-user-plus" />}
                <span>{t("Sign up")}</span>
              </button>

              <div className="auth-legal-links">
                <Link to="/terms">{t("Terms & Conditions")}</Link>
                <span />
                <Link to="/Privacy">{t("Privacy Policy")}</Link>
              </div>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Register;
